import { Icon } from '../../components/Icons.jsx';

/* ============================================================
   Research structured report blocks (.rb-*)
   Theme-agnostic: styled only via CSS variables so the same
   markup renders on the dark on-screen report and the light
   PDF export (see content-plan.css + research-print.css).
   ============================================================ */

const TIER_CLASS = {
  global: 'rb-tier-global',
  regional: 'rb-tier-regional',
  local: 'rb-tier-local',
  newcomer: 'rb-tier-newcomer',
};

const LEVERAGE_CLASS = {
  high: 'rb-plev-high',
  med: 'rb-plev-med',
  low: 'rb-plev-low',
};

/* Small mono-uppercase eyebrow header shared by every block. */
function RbBlock({ label, className, children }) {
  return (
    <div className={className ? `rb-block ${className}` : 'rb-block'}>
      <span className="rb-eyebrow">{label}</span>
      {children}
    </div>
  );
}

/* ---- Competitor cards ---- */
export function CompetitorCards({ items, t }) {
  if (!items || items.length === 0) return null;
  return (
    <RbBlock label={t('research.blockCompetitors')}>
      <div className="rb-comp-grid">
        {items.map((c, i) => {
          const tierClass = TIER_CLASS[c.tier] || 'rb-tier-newcomer';
          return (
            <div className="rb-comp" key={i}>
              <span className={`rb-tier ${tierClass}`}>
                {t('research.tier_' + c.tier)}
              </span>
              <span className="rb-comp-nm">
                {c.name}
                {c.region ? <span className="rb-comp-cc">{c.region}</span> : null}
              </span>
              {c.note ? <span className="rb-comp-ln">{c.note}</span> : null}
              {c.priceBand ? <span className="rb-comp-pb">{c.priceBand}</span> : null}
              {c.opportunity ? (
                <span className="rb-comp-opp">
                  <Icon name="check" size={11} stroke={2} />
                  {c.opportunity}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </RbBlock>
  );
}

/* ---- Archetype cards ---- */
export function ArchetypeCards({ items, t }) {
  if (!items || items.length === 0) return null;
  return (
    <RbBlock label={t('research.blockArchetypes')}>
      <div className="rb-arch-grid">
        {items.map((a, i) => (
          <div className="rb-arch" key={i}>
            <span className="rb-arch-nm">{a.name}</span>
            {a.note ? <span className="rb-arch-ln">{a.note}</span> : null}
          </div>
        ))}
      </div>
    </RbBlock>
  );
}

/* ---- Discovery channel bars (horizontal, share-weighted) ---- */
export function ChannelBars({ items, colors, t }) {
  if (!items || items.length === 0) return null;
  const sorted = [...items].sort((a, b) => b.share - a.share);
  const max = sorted.reduce((m, c) => Math.max(m, c.share), 0) || 1;
  return (
    <RbBlock label={t('research.blockChannels')}>
      <div className="rb-chan-list">
        {sorted.map((c, i) => {
          const fill = colors && colors.length ? colors[i % colors.length] : 'var(--accent)';
          return (
            <div className="rb-chan" key={i}>
              <span className="rb-chan-l">{c.label}</span>
              <span className="rb-chan-track">
                <span
                  className="rb-chan-bar"
                  style={{ width: `${(c.share / max) * 100}%`, background: fill }}
                />
              </span>
              <span className="rb-chan-v mono">{`${Math.round(c.share)}%`}</span>
            </div>
          );
        })}
      </div>
    </RbBlock>
  );
}

/* ---- Content-angle pillars (by leverage) ---- */
export function AnglePillars({ items, colors, t }) {
  if (!items || items.length === 0) return null;
  return (
    <RbBlock label={t('research.blockAngles')}>
      <div className="rb-pillar-list">
        {items.map((a, i) => {
          const levClass = LEVERAGE_CLASS[a.leverage] || 'rb-plev-low';
          const dotColor =
            colors && colors.length ? colors[i % colors.length] : undefined;
          return (
            <div className="rb-pillar" key={i}>
              <span
                className={`rb-pdot ${levClass}`}
                style={dotColor ? { background: dotColor } : undefined}
              />
              <span className="rb-pn">{a.label}</span>
              <span className={`rb-plev ${levClass}`}>
                {t('research.leverage_' + a.leverage)}
              </span>
            </div>
          );
        })}
      </div>
    </RbBlock>
  );
}

/* ---- Phased roadmap + optional KPI threshold strips ---- */
export function Roadmap({ phases, kpis, t }) {
  const hasPhases = phases && phases.length > 0;
  const hasKpis = kpis && kpis.length > 0;
  if (!hasPhases && !hasKpis) return null;
  return (
    <RbBlock label={t('research.blockRoadmap')}>
      {hasPhases ? (
        <div className="rb-road">
          {phases.map((p, i) => (
            <div className="rb-phase" key={i}>
              <span className="rb-phl">{p.label}</span>
              <span className="rb-pht">{p.title}</span>
              {p.items && p.items.length ? (
                <ul>
                  {p.items.map((it, j) => (
                    <li key={j}>{it}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {hasKpis ? (
        <div className="rb-kpi-sub">
          <span className="rb-eyebrow">{t('research.blockKpiThresholds')}</span>
          <div className="rb-kstrip-list">
            {kpis.map((k, i) => (
              <div className="rb-kstrip" key={i}>
                <span className="rb-kstrip-m">{k.metric}</span>
                <span className="rb-kstrip-t mono">{k.target}</span>
                <span className="rb-kstrip-r">{k.trigger}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </RbBlock>
  );
}

/* ---- Unit-economics table (metric · now · target) ---- */
export function EconomicsTable({ data, t }) {
  if (!data || !data.units || data.units.length === 0) return null;
  const hasTarget = data.units.some((u) => u.target);
  return (
    <RbBlock label={t('research.blockEconomics')}>
      <div className="cpr-tblwrap">
      <table className="rb-econ">
        <thead>
          <tr>
            <th>{t('research.blockUnitEconomics')}</th>
            <th>{t('research.econNow')}</th>
            {hasTarget ? <th>{t('research.econTarget')}</th> : null}
          </tr>
        </thead>
        <tbody>
          {data.units.map((u, i) => (
            <tr key={i}>
              <td className="rb-econ-l">{u.label}</td>
              <td className="rb-econ-v mono">{u.value}</td>
              {hasTarget ? <td className="rb-econ-t mono">{u.target || '—'}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </RbBlock>
  );
}

/* ---- Positioning: value proposition + differentiation angles ---- */
export function PositioningBlock({ data, t }) {
  if (!data || (!data.statement && (!data.angles || data.angles.length === 0))) return null;
  return (
    <RbBlock label={t('research.blockPositioning')}>
      {data.statement ? <p className="rb-pos-stmt">{data.statement}</p> : null}
      {data.angles && data.angles.length ? (
        <div className="rb-pos-grid">
          {data.angles.map((a, i) => (
            <div className="rb-pos" key={i}>
              <span className="rb-pos-t">{a.title}</span>
              {a.note ? <span className="rb-pos-n">{a.note}</span> : null}
            </div>
          ))}
        </div>
      ) : null}
    </RbBlock>
  );
}
